import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalizedTo } from "../hooks/useLocaleNavigation";

/**
 * Localized 404 (issue #221 / #243 review).
 *
 * Rendered by the `*` catch-all NESTED under `/:locale`, so an unknown but
 * validly-localized route (e.g. `/en-us/not-a-route`) shows a 404 IN PLACE
 * rather than falling through to the top-level bookmark-migration redirect,
 * which double-prefixed the path (`/sv-se/en-us/not-a-route`) and could loop on
 * a same-URL <Navigate> for default-locale misses (Codex P2 on #243).
 */
const NotFoundPage: React.FC = () => {
	const { t } = useTranslation();
	const localizedTo = useLocalizedTo();

	return (
		<div
			className="container mx-auto px-lg py-3xl text-center"
			style={{ maxWidth: "var(--container-max-width)" }}
			data-testid="not-found"
		>
			<p className="text-primary font-serif text-6xl md:text-7xl font-semibold mb-md">404</p>
			<h1 className="font-serif text-2xl md:text-3xl font-semibold text-text-primary mb-sm">
				<span data-i18n-key="notFound.title">{t("notFound.title", "Page not found")}</span>
			</h1>
			<p className="text-text-secondary text-base md:text-lg max-w-xl mx-auto mb-2xl leading-relaxed">
				<span data-i18n-key="notFound.body">
					{t("notFound.body", "The page you're looking for doesn't exist or has moved.")}
				</span>
			</p>
			<Link
				to={localizedTo("/")}
				data-testid="not-found-home-link"
				className="inline-flex items-center px-lg py-sm rounded-full bg-primary text-background-primary hover:opacity-90 transition-opacity min-h-[44px]"
			>
				<span data-i18n-key="notFound.home">{t("notFound.home", "Back to home")}</span>
			</Link>
		</div>
	);
};

export default NotFoundPage;
